import { FormattedMessage } from 'react-intl';

/**
 * Generic utility to update a tree data structure with changes
 * 
 * @param {Object} dataMap - The data map to update
 * @param {Array} allItems - All items from the latest data
 * @param {Array} newItems - New items to add
 * @param {Array} updatedItems - Existing items that were updated
 * @param {Object} expansionState - Current expansion state to preserve
 * @param {Object} options - Configuration options
 * @returns {void}
 */
export const updateTreeDataMap = (dataMap, allItems, newItems, updatedItems, expansionState, options = {}) => {
  const {
    idField = 'id',                     // Field name for unique ID
    parentIdField = 'parentId',         // Field name for parent ID
    childrenField = 'children',         // Field name for children array
    rootParentValue = null,             // Value that indicates a root item
    createItemObject = null,            // Function to create a new item object
    updateItemObject = null,            // Function to update an existing item
    getItemLevel = (item) => item.level || 0,  // Function to get item level
    styleForLevel = null,               // Function to get style for level
    nullParentId = '00000000-0000-0000-0000-000000000000'  // Value for null parent ID
  } = options;

  // First, update existing items
  updatedItems.forEach(item => {
    const id = item[idField];
    if (dataMap.data[id] && updateItemObject) {
      dataMap.data[id] = updateItemObject(dataMap.data[id], item);
    }
  });
  
  // Then, add new items
  newItems.forEach(item => {
    const id = item[idField];
    const parentId = item[parentIdField];
    
    // Create the item object
    let itemObj;
    if (createItemObject) {
      itemObj = createItemObject(item);
    } else {
      // Default implementation
      itemObj = {
        ...item,
        [childrenField]: [],
        isExpanded: false,
        checked: false,
        level: 0,
        hasChildren: false
      };
    }
    
    // Add to dataMap
    dataMap.data[id] = itemObj;
    
    // If it's a root item, add to ids array
    if (!parentId || parentId === nullParentId || parentId === rootParentValue) {
      dataMap.ids.push(id);
      // Also add to sortedIds and filteredIds if not already there
      if (!dataMap.sortedIds.includes(id)) {
        dataMap.sortedIds.push(id);
      }
      if (!dataMap.filteredIds.includes(id)) {
        dataMap.filteredIds.push(id);
      }
    } else {
      // It's a child item, add to parent's children
      if (dataMap.data[parentId]) {
        // Add to parent's children array
        dataMap.data[parentId][childrenField].push(itemObj);
        // Update parent's hasChildren flag
        dataMap.data[parentId].hasChildren = true;
        
        // If parent is expanded, add this child to visible rows
        if (dataMap.data[parentId].isExpanded) {
          const parentIndex = dataMap.sortedIds.indexOf(parentId);
          if (parentIndex !== -1) {
            // Find the right position to insert (after parent and its existing visible children)
            let insertIndex = parentIndex + 1;
            while (
              insertIndex < dataMap.sortedIds.length && 
              dataMap.data[dataMap.sortedIds[insertIndex]] && 
              isDescendantOf(dataMap.data[dataMap.sortedIds[insertIndex]], dataMap.data[parentId], dataMap, options)
            ) {
              insertIndex++;
            }
            dataMap.sortedIds.splice(insertIndex, 0, id);
            dataMap.filteredIds.splice(insertIndex, 0, id);
          }
        }
        
        // Update the level based on parent's level
        dataMap.data[id].level = getItemLevel(dataMap.data[parentId]) + 1;
        
        // Update the style based on level if styleForLevel is provided
        if (styleForLevel) {
          dataMap.data[id].style = styleForLevel(dataMap.data[id].level);
        }
      }
    }
  });
  
  // Rebuild the parent-child relationships for all items
  rebuildTreeHierarchy(dataMap, allItems, options);
  
  // Restore expansion state
  Object.keys(expansionState).forEach(id => {
    if (dataMap.data[id]) {
      dataMap.data[id].isExpanded = true;
    }
  });
};

/**
 * Check if an item is a descendant of another item
 */
export const isDescendantOf = (possibleDescendant, ancestor, dataMap, options = {}) => {
  const {
    parentIdField = 'parentId',
    idField = 'id'
  } = options;
  
  if (!possibleDescendant || !ancestor) return false;
  
  let current = possibleDescendant;
  while (current[parentIdField]) {
    if (current[parentIdField] === ancestor[idField]) return true;
    const parent = dataMap.data[current[parentIdField]];
    if (!parent) break;
    current = parent;
  }
  return false;
};

/**
 * Rebuild the tree hierarchy
 */
export const rebuildTreeHierarchy = (dataMap, allItems, options = {}) => {
  const {
    idField = 'id',
    parentIdField = 'parentId',
    childrenField = 'children',
    nullParentId = '00000000-0000-0000-0000-000000000000'
  } = options;
  
  // Clear all children arrays
  Object.keys(dataMap.data).forEach(id => {
    dataMap.data[id][childrenField] = [];
    dataMap.data[id].hasChildren = false;
  });
  
  // Rebuild children arrays
  allItems.forEach(item => {
    const id = item[idField];
    const parentId = item[parentIdField];
    
    if (parentId && 
        parentId !== nullParentId && 
        dataMap.data[parentId] && 
        dataMap.data[id]) {
      
      dataMap.data[parentId][childrenField].push(dataMap.data[id]);
      dataMap.data[parentId].hasChildren = true;
    }
  });
};

/**
 * Create a site object (specific to SiteMapping)
 */
export const createSiteObject = (site, level = 0, isExpanded = false) => ({
  id: site.id,
  name: site.name,
  buildingId: site.building_id,
  status: site.building_id ? 
    <FormattedMessage id="MAPPED" defaultMessage="Mapped"/> : 
    <FormattedMessage id="UNMAPPED" defaultMessage="Unmapped"/>,
  parentId: site.parent_id,
  children: [],
  isExpanded,
  checked: false,
  level,
  hasChildren: false
});

/**
 * Update a site object (specific to Raptor SiteMapping)
 */
export const updateSiteObject = (existingSite, updatedSite) => ({
  ...existingSite,
  buildingId: updatedSite.building_id,
  status: updatedSite.building_id ? 
    <FormattedMessage id="MAPPED" defaultMessage="Mapped"/> : 
    <FormattedMessage id="UNMAPPED" defaultMessage="Unmapped"/>
});

/**
 * Update a site object (specific to Singlewire SiteMapping)
 */
export const updateSinglewireSiteObject = (existingSite, updatedSite) => ({
  ...existingSite,
  singlewireSiteName: updatedSite.singlewire_site_name,
  status: updatedSite.singlewire_site_name ? 
    <FormattedMessage id="MAPPED" defaultMessage="Mapped"/> : 
    <FormattedMessage id="UNMAPPED" defaultMessage="Unmapped"/>
});

export function ensureChildSelectionConsistency(dataMap) {
  // First pass: collect all selected parent IDs
  const selectedParents = Object.keys(dataMap.selectedIds).filter(id => 
    dataMap.data[id] && dataMap.data[id].hasChildren
  );
  
  // Second pass: ensure all children of selected parents are also selected
  selectedParents.forEach(parentId => {
    const parent = dataMap.data[parentId];
    if (parent && parent.children) {
      parent.children.forEach(child => {
        // Select the child
        dataMap.selectedIds[child.id] = 1;
        if (dataMap.data[child.id]) {
          dataMap.data[child.id].checked = true;
          
          // Recursively select children of children
          if (dataMap.data[child.id].hasChildren) {
            ensureChildSelectionConsistency({
              ...dataMap,
              selectedIds: { [child.id]: 1 }
            });
          }
        }
      });
    }
  });
} 